import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNotifications, useMarkAsRead, useMarkAllRead, useDeleteNotification } from '../../api/useNotifications';
import { getLocalizedRelativeTime } from '../../utils/dayjsLocalization';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Button,
  Paper,
  Stack,
  Container,
  Skeleton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const Notifications = () => {
  const { t, i18n } = useTranslation(['notifications', 'common']);
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const { token } = useAuth();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Remote data - with optimized caching
  const { data: notifResp, isLoading, refetch } = useNotifications(token, { 
    limit: 100,
    staleTime: 30000, // 30 seconds
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Allow refetch when mounting the page
    refetchOnReconnect: false
  });
  const notifications = notifResp?.data || [];

  const { mutate: markAsRead, isPending: markingRead } = useMarkAsRead(token);
  const { mutate: markAllRead, isPending: markingAllRead } = useMarkAllRead(token);
  const { mutate: deleteNotification, isPending: deleting } = useDeleteNotification(token);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getFilteredNotifications = () => {
    if (tabValue === 0) {
      return notifications;
    } else if (tabValue === 1) {
      return notifications.filter(notification => !notification.isRead);
    } else {
      return notifications.filter(notification => notification.isRead);
    }
  };

  const handleMarkAllRead = () => {
    markAllRead(undefined, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: t('notifications:all_notifications_marked_read'),
          severity: 'success'
        });
        refetch();
      },
      onError: (error) => {
        console.error('Error marking all as read:', error);
        setSnackbar({
          open: true,
          message: t('notifications:error_marking_all_read'),
          severity: 'error'
        });
      }
    });
  };

  const handleMarkAsRead = (notificationId) => {
    markAsRead(notificationId, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: t('notifications:notification_marked_read'),
          severity: 'success'
        });
        refetch();
      },
      onError: (error) => {
        console.error('Error marking as read:', error);
        setSnackbar({
          open: true,
          message: t('notifications:error_marking_read'),
          severity: 'error'
        });
      }
    });
  };

  const handleDeleteClick = (notification) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (notificationToDelete) {
      deleteNotification(notificationToDelete.id, {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: t('notifications:notification_deleted'),
            severity: 'success'
          });
          setDeleteDialogOpen(false);
          setNotificationToDelete(null);
          refetch();
        },
        onError: (error) => {
          console.error('Error deleting notification:', error);
          setSnackbar({
            open: true,
            message: t('notifications:error_deleting'),
            severity: 'error'
          });
        }
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNotificationToDelete(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'workflow':
        return <AssignmentIcon />;
      case 'document':
        return <FolderIcon />;
      case 'user':
        return <PersonIcon />;
      case 'system':
        return <InfoIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getEmptyStateMessage = () => {
    if (tabValue === 0) {
      return t('notifications:no_notifications');
    } else if (tabValue === 1) {
      return t('notifications:no_unread_notifications');
    } else {
      return t('notifications:no_read_notifications');
    }
  };

  return (
    <Container maxWidth="md" dir={isRtl ? 'rtl' : 'ltr'} sx={{ direction: isRtl ? 'rtl' : 'ltr', py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          {t('notifications:title')}
        </Typography>
        <Button
          startIcon={<CheckCircleIcon />}
          onClick={handleMarkAllRead}
          size="small"
          color="primary"
          disabled={markingAllRead || getFilteredNotifications().filter(n => !n.isRead).length === 0}
          sx={{ minWidth: 140 }}
        >
          {markingAllRead ? t('common:loading') : t('notifications:mark_all_read')}
        </Button>
      </Stack>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label={t('notifications:all')} />
          <Tab label={t('notifications:unread')} />
          <Tab label={t('notifications:read')} />
        </Tabs>
      </Paper>

      <Stack spacing={2}>
        {isLoading && (
          Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton key={idx} variant="rectangular" height={72} />
          ))
        )}

        {!isLoading && getFilteredNotifications().length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`
          }}>
            <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {getEmptyStateMessage()}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('notifications:settings_message')}
            </Typography>
          </Box>
        )}

        {!isLoading && getFilteredNotifications().map((n) => {
          console.log('Rendering notification:', n);
          const params = {
            user: n.fromUser?.full_name || n.fromUser?.username || '',
            document: n.document?.reference_number || n.document?.title || '',
            docTitle: n.document?.title || '',
            docType: n.document?.type || ''
          };
          
          // Get translated title
          const getTranslatedTitle = (title) => {
            if (!title) return '';
            
            const titleKey = title.toLowerCase().replace(/\s+/g, '_');
            const translationKey = `notifications:titles.${titleKey}`;
            const translatedTitle = t(translationKey);
            
            // If translation doesn't exist, return original title
            return translatedTitle !== translationKey ? translatedTitle : title;
          };
          
          // Check if message already contains assigner name (for bulk assignments)
          const messageContainsAssigner = n.message && n.message.includes('assigned you a task');
          console.log('Notifications - Message:', n.message);
          console.log('Notifications - Contains assigner:', messageContainsAssigner);
          console.log('Notifications - Title:', n.title);
          
          const messageKeyMap = {
            'task assigned': messageContainsAssigner ? 'notifications:task_assigned_bulk_message' : 'notifications:task_assigned_message',
            'new task assigned': messageContainsAssigner ? 'notifications:task_assigned_bulk_message' : 'notifications:task_assigned_message',
            'task in progress': 'notifications:task_in_progress_message',
            'task forwarded': 'notifications:task_forwarded_message',
            'task completed': 'notifications:task_completed_message',
            'task rejected': 'notifications:task_rejected_message',
            'task accepted': 'notifications:task_accepted_message'
          };
          
          let msg;
          if (messageContainsAssigner && (n.title?.toLowerCase() === 'task assigned' || n.title?.toLowerCase() === 'new task assigned')) {
            // Extract document reference from the message
            const docMatch = n.message.match(/document\s+([^\s]+)/);
            const documentRef = docMatch ? docMatch[1].trim() : params.document;
            msg = t('notifications:task_assigned_bulk_message', { 
              user: params.user, 
              document: documentRef 
            });
          } else {
            msg = messageKeyMap[n.title?.toLowerCase()] ? t(messageKeyMap[n.title.toLowerCase()], params) : n.message;
          }
          const translatedTitle = getTranslatedTitle(n.title);
          console.log('Notification message:', msg);
          console.log('Translated title:', translatedTitle);

          return (
            <Paper
              key={n.id}
              elevation={n.isRead ? 0 : 2}
              sx={{
                p: 2,
                bgcolor: n.isRead ? 'background.paper' : alpha(theme.palette.primary.main, 0.05),
                borderRight: isRtl ? 4 : 0,
                borderLeft: isRtl ? 0 : 4,
                borderColor: n.isRead ? 'transparent' : 'primary.main',
                display: 'flex',
                flexDirection: isRtl ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: 2,
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: n.isRead ? 2 : 4
                }
              }}
            >
              {/* Unread indicator */}
              {!n.isRead && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: isRtl ? 'auto' : 8,
                  left: isRtl ? 8 : 'auto',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  zIndex: 1
                }} />
              )}

              <Avatar 
                sx={{ 
                  bgcolor: n.isRead ? 'grey.400' : 'primary.main',
                  color: n.isRead ? 'grey.700' : 'white',
                  flexShrink: 0,
                  width: 48,
                  height: 48
                }}
              >
                {getNotificationIcon(n.type)}
              </Avatar>

              <Box flexGrow={1} sx={{ minWidth: 0 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 1,
                  flexDirection: isRtl ? 'row-reverse' : 'row'
                }}>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight={n.isRead ? 400 : 600}
                    sx={{ 
                      flexGrow: 1,
                      textAlign: isRtl ? 'right' : 'left',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {translatedTitle}
                  </Typography>
                  {n.priority && (
                    <Chip
                      label={t(`notifications:priority_${n.priority}`)}
                      size="small"
                      color={getPriorityColor(n.priority)}
                      variant="outlined"
                    />
                  )}
                </Box>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  mb={0.5}
                  sx={{ 
                    textAlign: isRtl ? 'right' : 'left',
                    lineHeight: 1.4
                  }}
                >
                  {msg}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  color="text.disabled"
                  sx={{ 
                    textAlign: isRtl ? 'right' : 'left',
                    display: 'block'
                  }}
                >
                  {getLocalizedRelativeTime(n.createdAt, i18n.language)}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                {!n.isRead && (
                  <IconButton 
                    size="small" 
                    onClick={() => handleMarkAsRead(n.id)}
                    disabled={markingRead}
                    color="primary"
                    sx={{ 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2)
                      }
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton 
                  size="small" 
                  onClick={() => handleDeleteClick(n)}
                  disabled={deleting}
                  color="error"
                  sx={{ 
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.2)
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('notifications:delete_notification')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('notifications:delete_confirm')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} color="inherit">
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('common:loading') : t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Notifications; 